<template>
  <button
    class="common-button"
    :disabled="disabled || loading"
    :class="[
      disabled ? 'disabled' : '',
      loading ? 'loading' : '',
      color,
      size,
      filled ? 'filled' : '',
      transparent ? 'transparent' : '',
      dashed ? 'dashed' : '',
      iconPosition
    ]"
  >
    <RefreshCcw class="loading-icon" v-if="loading" />
    <template v-else>
      <slot />
      <CommonIcon
        class="common-button-icon"
        v-if="icon"
        :name="icon"
        :stroke-width="1.5"
        :size="16"
      />
    </template>
  </button>
</template>

<script setup lang="ts">
import { RefreshCcw } from 'lucide-vue-next'
import CommonIcon, { type Icons } from './CommonIcon.vue'

withDefaults(
  defineProps<{
    filled?: boolean
    transparent?: boolean
    dashed?: boolean
    iconPosition?: 'left' | 'right'
    color?: 'primary' | 'dark' | 'success' | 'warning' | 'danger'
    size?: 'small' | 'medium'
    icon?: Icons
    loading?: boolean
    disabled?: boolean
  }>(),
  {
    color: 'primary',
    size: 'medium',
    iconPosition: 'right'
  }
)
</script>

<style scoped lang="scss">
.common-button {
  transition: 600ms cubic-bezier(0.19, 1, 0.22, 1);
  border-radius: 5px;
  cursor: pointer;
  background-color: transparent;
  border: var(--vc-primary) solid 1px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  font-family: 'Roboto';
  font-weight: 500;
}

.disabled {
  background-color: rgba(var(--vcrgb-dark), 0.1) !important;
  color: rgba(var(--vcrgb-dark), 0.4) !important;
  pointer-events: none;
  border-color: rgba(var(--vcrgb-dark), 0.1) !important;
}

.loading-icon {
  animation: rotation 1s infinite cubic-bezier(0.19, 1, 0.22, 1);
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-360deg);
  }
}

.left {
  flex-direction: row-reverse;
}

.filled {
  border: 0;
}

.dashed {
  border-style: dashed;
}

.small {
  padding: 5px 10px;
}

.medium {
  padding: 15px 20px;
}

.loading {
  background-color: var(--vc-dark);
}

@mixin color($class: primary) {
  $background: var(--vc-#{$class});
  $background-rgb: var(--vcrgb-#{$class});

  .#{$class} {
    color: $background;
    border-color: $background;
  }

  .#{$class}:hover {
    transition: 600ms cubic-bezier(0.19, 1, 0.22, 1);
    color: var(--vc-white) !important;
    background-color: color-mix(in srgb, $background, #000 5%) !important;
  }

  .#{$class}.filled {
    background-color: $background;
    color: var(--vc-white);
  }

  .#{$class}.transparent {
    background-color: rgba($background-rgb, 0.1);
    color: $background;
    border: 0;
  }

  .#{$class}.loading {
    cursor: default;
    pointer-events: none;
    background-color: rgba(var(--vcrgb-dark), 0.1);
    color: rgba(var(--vcrgb-dark), 0.4);
    border: 0;
  }

  .#{$class}.loading:hover {
    background-color: rgba(var(--vcrgb-dark), 0.11) !important;
    color: rgba(var(--vcrgb-dark), 0.4) !important;
  }
}

@include color();
@include color(dark);
@include color(success);
@include color(warning);
@include color(danger);
</style>
